import React from 'react';
import { Box, Button, Card, Container, Grid, Typography } from '@material-ui/core';

const renderSlideshow = (sectionConfig = {}, currentSlideIndex = 0) => {
    if (!sectionConfig.slideshow?.enabled || !sectionConfig.slideshow?.images?.length) {
        return null;
    }

    return (
        <Box style={sectionConfig.slideshow?.containerStyles || {}}>
            {sectionConfig.slideshow.images.map((image, index) => (
                <img
                    key={`hero-slide-${index}`}
                    src={image.url}
                    alt={image.alt || `hero-slide-${index}`}
                    style={{
                        ...(sectionConfig.slideshow?.imageStyles || {}),
                        ...(index === currentSlideIndex ? sectionConfig.slideshow?.activeImageStyles || {} : sectionConfig.slideshow?.inactiveImageStyles || {})
                    }}
                />
            ))}
            <Box style={sectionConfig.slideshow?.overlayStyles || {}} />
        </Box>
    );
};

const renderBackgroundLayers = (layers = []) => {
    if (!Array.isArray(layers) || !layers.length) {
        return null;
    }

    return layers.map((layer, index) => (
        <Box
            key={`hero-bg-layer-${index}`}
            className={layer.className || undefined}
            style={layer.styles || {}}
        />
    ));
};

const renderHeroButtons = (buttons = [], groupStyles = {}, layoutContext = {}) => {
    const { applyHoverEffect, removeHoverEffect, handleHeroButtonClick, getIcon } = layoutContext;
    const { getAnimationProps } = layoutContext;

    if (!Array.isArray(buttons) || buttons.length === 0) {
        return null;
    }

    return (
        <Box style={groupStyles || {}}>
            {buttons.map((buttonConfig, buttonIndex) => (
                (() => {
                    const animationProps = typeof getAnimationProps === 'function' ? getAnimationProps(buttonConfig.animation, buttonIndex) : {};
                    const iconElement = buttonConfig.icon ? getIcon?.(buttonConfig.icon, { fontSize: '1rem' }) : null;
                    const isEndIcon = buttonConfig.iconPosition === 'end';
                    return (
                <Button
                    key={`hero-button-${buttonIndex}`}
                    variant={buttonConfig.variant || 'contained'}
                    color={buttonConfig.color || 'primary'}
                    className={animationProps.className}
                    data-anim={animationProps.dataAnim}
                    data-delay={animationProps.dataDelay}
                    data-trigger-once={animationProps.dataTriggerOnce}
                    style={{ ...(buttonConfig.styles || {}), ...(animationProps.style || {}) }}
                    onMouseEnter={(event) => applyHoverEffect?.(event, buttonConfig.hoverStyles || {})}
                    onMouseLeave={(event) => removeHoverEffect?.(event, buttonConfig.styles || {})}
                    onClick={() => handleHeroButtonClick?.(buttonConfig)}
                    startIcon={!isEndIcon ? iconElement : null}
                    endIcon={isEndIcon ? iconElement : null}
                >
                    {buttonConfig.text}
                </Button>
                    );
                })()
            ))}
        </Box>
    );
};

const renderQuickCards = (sectionConfig = {}, layoutContext = {}) => {
    const cards = sectionConfig.quickCards?.items || [];
    if (!cards.length) {
        return null;
    }

    const { applyHoverEffect, removeHoverEffect, getIcon, isDesktop } = layoutContext;
    const cardStyles = sectionConfig.quickCards?.cardStyles || {};
    const resolvedContainerStyles = isDesktop
        ? (sectionConfig.quickCards?.containerStyles || {})
        : {
            ...(sectionConfig.quickCards?.containerStyles || {}),
            ...(sectionConfig.quickCards?.mobileContainerStyles || {})
        };
    const resolvedRowStyles = isDesktop
        ? (sectionConfig.quickCards?.rowStyles || {})
        : {
            ...(sectionConfig.quickCards?.rowStyles || {}),
            ...(sectionConfig.quickCards?.mobileRowStyles || {})
        };

    return (
        <Box style={resolvedContainerStyles}>
            <Box style={resolvedRowStyles}>
                {cards.map((card, index) => (
                    <Card
                        key={`hero-quick-card-${index}`}
                        style={{
                            ...(cardStyles || {}),
                            ...(card.styles || {})
                        }}
                        onMouseEnter={(event) => applyHoverEffect?.(event, card.hoverStyles || sectionConfig.quickCards?.cardHoverStyles || {})}
                        onMouseLeave={(event) => removeHoverEffect?.(event, { ...(cardStyles || {}), ...(card.styles || {}) })}
                    >
                        {card.image?.url && (
                            <Box style={sectionConfig.quickCards?.imageWrapperStyles || {}}>
                                <img
                                    src={card.image.url}
                                    alt={card.image.alt || card.title || `quick-card-${index}`}
                                    style={{
                                        ...(sectionConfig.quickCards?.imageStyles || {}),
                                        ...(card.image.styles || {})
                                    }}
                                />
                            </Box>
                        )}
                        <Box style={sectionConfig.quickCards?.contentStyles || {}}>
                            <Box style={sectionConfig.quickCards?.titleRowStyles || {}}>
                                {card.icon && (
                                    <Box style={sectionConfig.quickCards?.iconWrapperStyles || {}}>
                                        {getIcon?.(card.icon, {
                                            style: {
                                                ...(sectionConfig.quickCards?.iconStyles || {}),
                                                ...(card.iconStyles || {})
                                            }
                                        })}
                                    </Box>
                                )}
                                <Typography style={sectionConfig.quickCards?.titleStyles || {}}>
                                    {card.title}
                                </Typography>
                            </Box>
                            {card.subtitle && (
                                <Typography style={sectionConfig.quickCards?.subtitleStyles || {}}>
                                    {card.subtitle}
                                </Typography>
                            )}
                        </Box>
                    </Card>
                ))}
            </Box>
        </Box>
    );
};

const HeroSplitEditorial = ({ sectionConfig = {}, layoutContext = {}, globalConfig = {} }) => {
    const {
        applyHoverEffect,
        removeHoverEffect,
        handleHeroButtonClick,
        getIcon,
        getAnimationProps,
        map,
        MapComponent,
        mapRef,
        mapExpanded,
        setMapExpanded,
        mapError,
        setMapError,
        currentSlideIndex
    } = layoutContext;

    const applyAnimation = (animationConfig, index = 0) => (
        typeof getAnimationProps === 'function' ? getAnimationProps(animationConfig, index) : {}
    );

    const heroTitleText = sectionConfig.title?.text ?? globalConfig.title;
    const heroSubtitleText = sectionConfig.subtitle?.text ?? globalConfig.subtitle;
    const heroDescriptionText = sectionConfig.description?.text ?? globalConfig.description;
    const subtitlePosition = sectionConfig.subtitle?.position || 'afterTitle';

    const shouldRenderMap = globalConfig.displayMap;
    const shouldRenderMedia = !shouldRenderMap && sectionConfig.media?.type === 'image';
    const mapConfig = globalConfig.map || {};
    const sectionAnimation = applyAnimation(sectionConfig.animation?.section);
    const containerAnimation = applyAnimation(sectionConfig.animation?.container);
    const contentAnimation = applyAnimation(sectionConfig.animation?.content);
    const mediaAnimation = applyAnimation(sectionConfig.media?.animation);

    const renderMapLegend = () => {
        if (!globalConfig.displayMapLegend || !Array.isArray(mapConfig.legend)) {
            return null;
        }

        return (
            <Box style={mapConfig.legendStyles || {}}>
                {mapConfig.legend.map((item, index) => (
                    <Box
                        key={`map-legend-${index}`}
                        style={{
                            ...(mapConfig.legendItemStyles || {}),
                            marginBottom: index < mapConfig.legend.length - 1 ? (mapConfig.legendItemStyles?.marginBottom || 2) : 0
                        }}
                    >
                        <span
                            style={{
                                ...(mapConfig.legendColorStyles || {}),
                                background: item.color,
                                border: `1px solid ${item.borderColor || item.color}`
                            }}
                        />
                        <span>{item.label}</span>
                    </Box>
                ))}
            </Box>
        );
    };

    const renderMapCard = () => {
        if (!shouldRenderMap || !MapComponent) {
            return null;
        }

        const cardAnimation = applyAnimation(sectionConfig.map?.animation);
        return (
            <Grid
                item
                xs={sectionConfig.gridItemMap?.xs || 12}
                md={mapExpanded ? 12 : (sectionConfig.gridItemMap?.md || 6)}
            >
                <Box style={mapConfig.styles || {}}>
                    <Card
                        className={cardAnimation.className}
                        data-anim={cardAnimation.dataAnim}
                        data-delay={cardAnimation.dataDelay}
                        data-trigger-once={cardAnimation.dataTriggerOnce}
                        style={{ ...(mapConfig.styles || {}), ...(cardAnimation.style || {}) }}
                    >
                        <Box style={mapConfig.headerStyles || {}}>
                            <Box style={mapConfig.headerContentStyles || {}}>
                                <Box style={mapConfig.statusDotStyles?.red || {}} />
                                <Box style={mapConfig.statusDotStyles?.yellow || {}} />
                                <Box style={mapConfig.statusDotStyles?.green || {}} />
                                <Typography
                                    variant={mapConfig.typography?.variant || 'h6'}
                                    style={mapConfig.titleStyles || {}}
                                >
                                    {mapConfig.title || `${globalConfig.title || ''} Map`}
                                </Typography>
                            </Box>
                            {mapExpanded ? (
                                <Button
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setMapExpanded?.(false);
                                    }}
                                    style={mapConfig.expandButton?.closeStyles || {}}
                                >
                                    {mapConfig.expandButton?.closeText || 'Close'}
                                </Button>
                            ) : (
                                <Button
                                    variant={mapConfig.expandButton?.variant || 'outlined'}
                                    onClick={() => setMapExpanded?.(true)}
                                    style={mapConfig.expandButton?.expandStyles || {}}
                                >
                                    {mapConfig.expandButton?.text || 'Expand Map'}
                                </Button>
                            )}
                        </Box>
                        <Box style={mapExpanded ? (mapConfig.contentExpandedStyles || {}) : (mapConfig.contentStyles || {})}>
                            <Box style={mapConfig.mapContainerStyles || {}}>
                                <div ref={mapRef} style={mapConfig.mapStyles || {}}>
                                    {(() => {
                                        try {
                                            if (mapError) {
                                                return (
                                                    <Box style={mapConfig.errorStyles || {}}>
                                                        {mapConfig.errorMessage || 'Map unavailable'}
                                                    </Box>
                                                );
                                            }
                                            if (map) {
                                                return <MapComponent {...map} height="100%" width="100%" />;
                                            }
                                            return (
                                                <Box style={mapConfig.loadingStyles || {}}>
                                                    {mapConfig.loadingMessage || 'Map loading...'}
                                                </Box>
                                            );
                                        } catch (error) {
                                            console.warn('Map component error:', error);
                                            setMapError?.(true);
                                            return (
                                                <Box style={mapConfig.errorStyles || {}}>
                                                    {mapConfig.errorMessage || 'Map unavailable'}
                                                </Box>
                                            );
                                        }
                                    })()}
                                    {renderMapLegend()}
                                </div>
                            </Box>
                        </Box>
                    </Card>
                </Box>
            </Grid>
        );
    };

    const renderMedia = () => {
        if (!shouldRenderMedia) {
            return null;
        }

        return (
            <Grid
                item
                xs={12}
                lg={sectionConfig.gridItemMap?.lg || sectionConfig.gridItem?.lg || 6}
                style={{ display: 'flex', alignItems: 'center' }}
            >
                <Box
                    className={mediaAnimation.className}
                    data-anim={mediaAnimation.dataAnim}
                    data-delay={mediaAnimation.dataDelay}
                    data-trigger-once={mediaAnimation.dataTriggerOnce}
                    style={{ ...(sectionConfig.media?.containerStyles || {}), ...(mediaAnimation.style || {}) }}
                >
                    <Box
                        style={{
                            borderRadius: '2rem',
                            overflow: 'hidden',
                            position: 'relative',
                            ...(sectionConfig.media?.imageWrapperStyles || {})
                        }}
                    >
                        <img
                            src={sectionConfig.media?.src}
                            alt={sectionConfig.media?.alt || 'hero-media'}
                            style={sectionConfig.media?.imageStyles || { width: '100%' }}
                        />
                        {sectionConfig.media?.overlayStyles && (
                            <Box style={sectionConfig.media?.overlayStyles} />
                        )}
                    </Box>
                    {sectionConfig.media?.infoCard?.display !== false && (
                        <Box style={sectionConfig.media?.infoCard?.containerStyles || {}}>
                            {sectionConfig.media?.infoCard?.label && (
                                <Typography style={sectionConfig.media?.infoCard?.labelStyles || {}}>
                                    {sectionConfig.media.infoCard.label}
                                </Typography>
                            )}
                            {sectionConfig.media?.infoCard?.title && (
                                <Typography style={sectionConfig.media?.infoCard?.titleStyles || {}}>
                                    {sectionConfig.media.infoCard.title}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            </Grid>
        );
    };

    return (
        <Box
            id="hero-section"
            className={sectionAnimation.className}
            data-anim={sectionAnimation.dataAnim}
            data-delay={sectionAnimation.dataDelay}
            data-trigger-once={sectionAnimation.dataTriggerOnce}
            style={{ ...(sectionConfig.styles || {}), ...(sectionAnimation.style || {}) }}
        >
            {renderBackgroundLayers(sectionConfig.backgroundLayers)}
            {renderSlideshow(sectionConfig, currentSlideIndex)}
            <Container
                maxWidth={false}
                disableGutters
                style={{
                    width: '100%',
                    ...(sectionConfig.containerStyles || {}),
                    ...(containerAnimation.style || {}),
                    position: 'relative',
                    zIndex: 2
                }}
                className={containerAnimation.className}
                data-anim={containerAnimation.dataAnim}
                data-delay={containerAnimation.dataDelay}
                data-trigger-once={containerAnimation.dataTriggerOnce}
            >
                <Grid
                    container
                    spacing={mapExpanded ? 0 : (sectionConfig.grid?.spacing || 2)}
                    alignItems={sectionConfig.grid?.alignItems || 'center'}
                >
                        <Grid
                            item
                            xs={12}
                            md={mapExpanded ? 0 : (sectionConfig.gridItem?.md || 6)}
                            style={{
                                display: mapExpanded ? 'none' : 'block'
                            }}
                        >
                            <Box
                                className={contentAnimation.className}
                                data-anim={contentAnimation.dataAnim}
                                data-delay={contentAnimation.dataDelay}
                                data-trigger-once={contentAnimation.dataTriggerOnce}
                                style={{
                                    ...(shouldRenderMap || shouldRenderMedia
                                        ? {}
                                        : (sectionConfig.noMapStyles || {})),
                                    ...(contentAnimation.style || {})
                                }}
                            >
                                {subtitlePosition === 'beforeTitle' && heroSubtitleText && (
                                    <Typography
                                        variant={sectionConfig.typography?.subtitle?.variant || 'h4'}
                                        style={sectionConfig.subtitle?.styles || {}}
                                    >
                                        {heroSubtitleText}
                                    </Typography>
                                )}
                                <Typography
                                    variant={sectionConfig.typography?.title?.variant || 'h1'}
                                    style={sectionConfig.title?.styles || {}}
                                >
                                    {heroTitleText}
                                </Typography>
                                {subtitlePosition !== 'beforeTitle' && heroSubtitleText && (
                                    <Typography
                                        variant={sectionConfig.typography?.subtitle?.variant || 'h4'}
                                        style={sectionConfig.subtitle?.styles || {}}
                                    >
                                        {heroSubtitleText}
                                    </Typography>
                                )}
                                {heroDescriptionText && (
                                    <Typography
                                        variant={sectionConfig.typography?.description?.variant || 'h6'}
                                        style={sectionConfig.description?.styles || {}}
                                    >
                                        {heroDescriptionText}
                                    </Typography>
                                )}
                                {renderHeroButtons(sectionConfig.buttons || [], sectionConfig.buttonGroupStyles, {
                                    applyHoverEffect,
                                    removeHoverEffect,
                                    handleHeroButtonClick,
                                    getIcon,
                                    getAnimationProps
                                })}
                            </Box>
                        </Grid>
                    
                    {renderMedia()}
                    {renderMapCard()}
                </Grid>
                {!mapExpanded && renderQuickCards(sectionConfig, layoutContext)}
            </Container>
            
        </Box>
    );
};

export default HeroSplitEditorial;
